/**
 * Campo das áreas de um cargo ou de um usuário (24/10/2026), ligado ao
 * react-hook-form. Guarda `string[]` de chaves de área.
 *
 * A área diz em que parte do Hub a pessoa entra; o que ela vê lá dentro continua
 * sendo decidido pelas permissões de cada módulo. Por isso o campo vem antes do
 * seletor de permissões, que desabilita os módulos das áreas não marcadas.
 *
 * `travadas` são as áreas que o usuário recebe do cargo: marcadas e sem como
 * desmarcar. O campo guarda só as demais, que são as adicionais.
 */
import { useEffect, useMemo } from 'react'
import { useController, useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { ShieldCheck } from 'lucide-react'
import { AREAS } from '@/core/areas/areas'
import { AreaArte } from '@/core/areas/AreaArte'
import { useI18n } from '@/core/i18n/useI18n'
import { LEAF_RADIUS_SM } from '../../styles/theme'
import { usePermissionCatalog } from './PermissionPicker'

const SEM_TRAVADAS: readonly string[] = []

export function RHFAreaPicker({
  name,
  ajuda,
  travadas = SEM_TRAVADAS,
  preSelecionarPadrao = false,
}: {
  name: string
  /** Texto de apoio abaixo da legenda. */
  ajuda?: string
  /** As do cargo: marcadas, travadas e fora do valor do campo. */
  travadas?: readonly string[]
  /** Só na CRIAÇÃO de usuário: pré-marca as áreas padrão do papel escolhido, as
   *  que cobrem o conjunto padrão de permissões. */
  preSelecionarPadrao?: boolean
}) {
  const { t } = useI18n()
  const { data } = usePermissionCatalog()
  const { field } = useController({ name })
  const watchedRole = useWatch({ name: 'role' }) as string | undefined
  const proprias: string[] = useMemo(() => (field.value as string[]) ?? [], [field.value])
  const travadasSet = useMemo(() => new Set(travadas), [travadas])

  useEffect(() => {
    if (!preSelecionarPadrao || !data || !watchedRole) return
    const padrao = data.areas_padrao?.[watchedRole]
    if (padrao) field.onChange(padrao)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelecionarPadrao, data, watchedRole])

  if (watchedRole === 'staff_admin') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: LEAF_RADIUS_SM, p: 1.5, color: 'text.secondary' }}>
        <ShieldCheck size={18} />
        <Typography variant="body2">{t('form.areaPicker.adminTodas')}</Typography>
      </Box>
    )
  }

  function alternar(area: string) {
    if (travadasSet.has(area)) return
    field.onChange(proprias.includes(area) ? proprias.filter((a) => a !== area) : [...proprias, area])
  }

  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
      <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
        {t('form.areaPicker.legenda')}
      </Typography>
      {ajuda && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
          {ajuda}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {AREAS.map((area) => {
          const travada = travadasSet.has(area.key)
          const marcada = travada || proprias.includes(area.key)
          return (
            <FormControlLabel
              key={area.key}
              disabled={travada}
              sx={{
                m: 0, pr: 1.5, pl: 0.5, py: 0.25,
                border: '1px solid', borderColor: marcada ? 'primary.main' : 'divider',
                borderRadius: LEAF_RADIUS_SM,
              }}
              control={<Checkbox size="small" checked={marcada} onChange={() => alternar(area.key)} sx={{ p: 0.75 }} />}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component="span" sx={{ display: 'inline-flex', color: marcada ? 'primary.main' : 'text.secondary' }}>
                    <AreaArte nos={area.arte} size={16} />
                  </Box>
                  <Typography variant="body2" component="span">{t(area.labelKey)}</Typography>
                  {travada && (
                    <Chip
                      size="small" variant="outlined" color="primary" label={t('form.areaPicker.doCargo')}
                      sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' } }}
                    />
                  )}
                </Box>
              }
            />
          )
        })}
      </Box>
    </Box>
  )
}
